import { useContext } from "react";
import { isEqual, omit, pick } from "lodash";
import {
  CREATOR_AND_UP,
  hasExternalCreator,
  RoleName,
} from "common/authorization";
import { OBJECT_TYPE, SUBMISSION_TYPE_MODULE } from "common/constants";
import {
  convertJSONSchemaObjectToFormStructure,
  SubmissionsFormInputDescription,
} from "common/services/formBuilderService";
import { GetSubmissionTypeForBuilderQuery } from "../../../../../generated/graphql";
import { AuthContext } from "../../../../Authorization/AuthContext";
import {
  DEFAULT_POSTAMBLE,
  DEFAULT_PREAMBLE,
  EDITOR_TYPE,
  editorTypeToFieldPermissionsMap,
  fieldTypeToSchemaMap,
  VIEWER_TYPE,
  viewerTypeToFieldPermissionsMap,
} from "./constants";
import { makeUniqueFieldName } from "./helpers";
import {
  FieldContext,
  InputFieldPath,
  SubmissionsBuilderField,
  SubmissionsBuilderFormDataStructure,
  SubmissionsBuilderGroupField,
} from "./types";

const isGroupField = (
  field: SubmissionsBuilderField,
): field is SubmissionsBuilderGroupField => {
  return field.inputType === "group" && "properties" in field;
};

export const deepDuplicateField = (
  name: string,
  originalField: SubmissionsBuilderField,
  appendCopySuffix = true,
): SubmissionsBuilderField => {
  const result: SubmissionsBuilderField = {
    // providing generic params to specify that result is NOT a Partial<>
    ...omit<SubmissionsBuilderField, ["id"]>(originalField, "id"),
    name,
    title:
      appendCopySuffix ? `${originalField.title} Copy` : originalField.title,
  };

  if (isGroupField(originalField) && originalField.properties.length > 0) {
    result.properties = originalField.properties.reduce((acc, field) => {
      const name = makeUniqueFieldName();
      acc.push(deepDuplicateField(name, field, false));
      return acc;
    }, [] as SubmissionsBuilderField[]);
  }

  return result;
};

export const getNameFromId = (id: string) => {
  if (id === "root") {
    return "";
  }

  const index = id.lastIndexOf("_");
  if (index === -1) {
    return "root"; // Underscore not found
  }

  return id.substring(index + 1);
};

export const withStopPropagation =
  (eventHandler: () => void) => (event: React.SyntheticEvent) => {
    event.stopPropagation();
    eventHandler();
  };

type Input = {
  name: string;
  inputType?: string;
  properties?: Array<Input>;
  required?: boolean;
};

export const getAllInputs = (
  inputs: ReadonlyArray<Input> | Array<Input>,
): Array<Input> => {
  const fields = [];
  for (const input of inputs) {
    if (input.properties?.length) {
      fields.push(...getAllInputs(input.properties));
    }
    fields.push(input);
  }
  return fields;
};

export const getAllInputNames = (
  inputs: ReadonlyArray<Input> | Array<Input>,
): Array<string> => {
  const flattenedInputs = getAllInputs(inputs);
  return flattenedInputs.map(input => input.name);
};

export const getValidPostamble = ({
  canCreateRoles,
  canCreateGroups,
  postamble,
}: Pick<SubmissionsBuilderFormDataStructure, "postamble"> & {
  canCreateGroups: { id: string; name: string; memberType: string }[];
  canCreateRoles: Array<RoleName>;
}) => {
  return hasExternalCreator({ canCreateRoles, canCreateGroups }) ?
      (postamble ?? DEFAULT_POSTAMBLE)
    : null;
};

export const getValidPreamble = ({
  canCreateRoles,
  canCreateGroups,
  preamble,
}: Pick<SubmissionsBuilderFormDataStructure, "preamble"> & {
  canCreateRoles: Array<RoleName>;
  canCreateGroups: { id: string; name: string; memberType: string }[];
}) => {
  return hasExternalCreator({ canCreateRoles, canCreateGroups }) ?
      (preamble ?? DEFAULT_PREAMBLE)
    : null;
};

export const generateDefaultValues = ({
  existingSubmissionType,
  versionId,
}: {
  existingSubmissionType?: GetSubmissionTypeForBuilderQuery["submissionType"];
  versionId: Maybe<string>;
}): Omit<
  SubmissionsBuilderFormDataStructure,
  "attachments" | "inputs" | "name" | "category"
> & {
  attachments: Array<{ type: OBJECT_TYPE; tags?: Array<string> }>;
  inputs: Array<SubmissionsFormInputDescription>;
} => {
  if (!existingSubmissionType || !versionId) {
    return {
      modules: [],
      inputs: [],
      attachments: [],
      canCreateRolesAndGroupIds: CREATOR_AND_UP,
      hiddenFromPublicByDefault: true,
    };
  }

  const { formStructure } = existingSubmissionType.versions.find(
    version => version.id === versionId,
  )!;

  const existingSubmissionTypeFields = pick(existingSubmissionType, [
    "name",
    "description",
    "category",
    "modules",
    "hiddenFromPublicByDefault",
    "successMessage",
    "preamble",
    "postamble",
  ]);

  return {
    ...existingSubmissionTypeFields,
    inputs: convertJSONSchemaObjectToFormStructure(formStructure),
    attachments: existingSubmissionType.attachments.map(
      ({ type, customMapIds }) => ({
        type,
        ...(customMapIds ? { customMapIds } : {}),
      }),
    ),
    notificationEmails:
      existingSubmissionType.notificationEmails?.join("\n") ?? null,
    canCreateRolesAndGroupIds: [
      ...existingSubmissionType.canCreate,
      ...(existingSubmissionType.canCreateGroups.map(group => group.id) ?? []),
    ],
  };
};

export const buildNotificationEmailsArray = ({
  canCreateRoles,
  canCreateGroups,
  notificationEmails,
}: {
  canCreateRoles: Array<RoleName>;
  notificationEmails?: Maybe<string>;
  canCreateGroups: { id: string; name: string; memberType: string }[];
}) => {
  return hasExternalCreator({ canCreateRoles, canCreateGroups }) ?
      // Need to filter out empty string
      (notificationEmails?.split("\n") ?? [])
        .map(email => email.trim())
        .filter(email => !!email)
    : null;
};

export const useIsSisdAndHidden = (modules: Array<SUBMISSION_TYPE_MODULE>) => {
  const { account } = useContext(AuthContext);

  return (
    modules.includes(
      SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE,
    ) && !!account?.publicPortal.hideSISD
  );
};

export const submissionHiddenFromPublicByDefaultToViewerType = (
  value: Maybe<boolean>,
) => {
  switch (value) {
    case true:
      return VIEWER_TYPE.TEAM;
    case false:
      return VIEWER_TYPE.TEAM_AND_GUESTS;
    default:
      return null;
  }
};

export const viewerTypeToSubmissionHiddenFromPublicByDefault = (
  value: Maybe<VIEWER_TYPE>,
) => {
  switch (value) {
    case VIEWER_TYPE.TEAM:
      return true;
    case VIEWER_TYPE.TEAM_AND_GUESTS:
      return false;
    default:
      return null;
  }
};

/**
 * Given an array of role names and a map from option keys to role names,
 * return the key of the option that matches the value set
 */
export const getInputOptionFromFieldPermissions = <T extends string>(
  value: Maybe<Array<RoleName>>,
  optionsMap: Record<T, Array<RoleName>>,
) => {
  if (!value || !value.length) return null;
  const valueSet = new Set(value);
  const match = Object.entries(optionsMap).find(([_, permissions]) => {
    const optionSet = new Set(permissions as Array<RoleName>);
    return isEqual(valueSet, optionSet);
  });
  if (match) return match[0] as keyof typeof optionsMap;
  return null;
};

/**
 * This utility function builds a schema map containing default input field values. To be appended
 * to the form when the user adds a new field.
 * - fieldTypeToSchemaMap is a static map of field types to their schema definitions
 * - granular field permissions are generated dynamically based on canCreate and
 *   hiddenFromPublicByDefault values
 */
export const buildFieldSchemaMap = ({
  fieldTypeName,
  canCreateRolesAndGroupIds,
  hiddenFromPublicByDefault,
}: {
  fieldTypeName: keyof typeof fieldTypeToSchemaMap;
  canCreateRolesAndGroupIds: Array<RoleName | string>;
  hiddenFromPublicByDefault: boolean;
}) => {
  // granular field permissions inherit permissions option from parent submission type
  const granularFieldPermissions =
    fieldTypeName !== "group" ?
      {
        viewOnCreate: canCreateRolesAndGroupIds,
        viewOnRetrieve:
          hiddenFromPublicByDefault ?
            viewerTypeToFieldPermissionsMap[VIEWER_TYPE.TEAM]
          : viewerTypeToFieldPermissionsMap[VIEWER_TYPE.TEAM_AND_GUESTS],
        canUpdate: editorTypeToFieldPermissionsMap[EDITOR_TYPE.TEAM],
      }
    : {};
  return {
    ...fieldTypeToSchemaMap[fieldTypeName],
    ...granularFieldPermissions,
  };
};

export function buildFieldContextsByNameMap(
  formName: string,
  formData: SubmissionsBuilderField[],
) {
  const fieldPathsByNames: Record<string, FieldContext> = {};

  const recurse = (
    currentObj: SubmissionsBuilderField[],
    currentPath: string = "inputs",
    rjsfIdPrefix: string = "root",
    titles: string[] = [formName],
  ) => {
    currentObj.forEach((child, index) => {
      const childPath = `${currentPath}.${index}` as InputFieldPath;
      const rjsfId = `${rjsfIdPrefix}_${child.name}`;
      const updatedTitles = [...titles, child.title];

      fieldPathsByNames[rjsfId] = {
        fieldIndex: index,
        fieldPath: childPath,
        rjsfId,
        titles: updatedTitles,
        columnHeaderOverride: child.columnHeaderOverride,
      };

      if (Array.isArray(child.properties) && child.properties.length > 0) {
        recurse(
          child.properties,
          `${childPath}.properties`,
          rjsfId,
          updatedTitles,
        );
      }
    });
  };

  recurse(formData);

  return fieldPathsByNames;
}
